import React, { useState } from 'react';
import { Platform } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Inline,
  IBoxProps,
} from '@styleseat/ui';
import { BrandColor, colors } from '@styleseat/brand';
import nonCriticalException from '../../../../modules/exceptionLogger';
import { CALENDAR_ROUTE, PROFILE_EDIT_ROUTE } from '../../../../route-names';
import ProAvatarIcon from '../../../ProAvatarIcon';
import {
  BodyText,
  Stack,
  Touchable,
} from '../../../ui';
import { Popover } from '../../../ui/Popover';
import { IRootDispatch, RootState } from '../../../../store/models';
import { useParams, useLinkTo } from '../../../../hooks';
import StateLink from '../../../StateLink';
import { addQueryStringParams } from '../../../../modules/url';

export const Hoverable = (props: IBoxProps) => {
  const [hover, setHover] = useState<boolean>(false);

  return (
    <Box
      {...props}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      color={hover ? colors.grey1 : props.color}
    />
  );
};

const ProHeaderDropdown = ({
  providerId,
}) => {
  const [open, setOpen] = useState<boolean>(false);

  const linkTo = useLinkTo();
  const dispatch = useDispatch<IRootDispatch>();
  const params = useParams() || {};
  const proName = useSelector<RootState, string>(state => (
    // eslint-disable-next-line eqeqeq
    state.user?.providers?.find(pro => pro.id == providerId)?.name
  ));
  const subProviders = useSelector<RootState, { id: number, name: string }[]>(state => (
    state.user?.providers
  )) || [];
  const {
    utm_source,
    utm_campaign,
    utm_content,
    utm_medium,
    utm_term,
  } = params;

  const proParams = {
    providerId,
  };
  const homeParams = {
    mode: 'week',
    ...params,
    providerId,
  };
  const utmParams = {
    utm_source,
    utm_campaign,
    utm_content,
    utm_medium,
    utm_term,
  };

  const handleToggleClick = () => {
    setOpen(!open);
  };

  const handleLogout = async () => {
    try {
      await dispatch.user.logout();
      if (Platform.OS === 'web') {
        window.location.assign('/');
      } else {
        linkTo('start', undefined, undefined, true);
      }
    } catch (e) {
      nonCriticalException(e, { from: 'ProHeaderDropdown', action: 'logout' });
    }
  };

  if (!providerId) {
    return null;
  }

  return (
    <Box position="relative">
      <Touchable onClick={handleToggleClick} testID="proAccountMenuToggle">
        <Inline alignItems="center" space={4}>
          <ProAvatarIcon
            providerId={providerId}
            sizeClass="extra-small"
          />
          {subProviders.length <= 1 ? null : (
            <BodyText color={BrandColor.Metal}>{proName}</BodyText>
          )}
        </Inline>
      </Touchable>
      {open ? (
        <Popover
          name="proHeaderDropdown"
          borderRadius={0}
          borderColor={BrandColor.Grey3}
          top={47}
          right={-5}
          onClose={() => setOpen(false)}
        >
          <Stack
            paddingY={12}
            minWidth={172}
          >
            <StateLink
              to={CALENDAR_ROUTE}
              params={homeParams}
            >
              <Hoverable
                paddingX={24}
                paddingY={10}
              >
                <BodyText
                  appearance="small"
                  color={BrandColor.Metal}
                >
                  Home
                </BodyText>
              </Hoverable>
            </StateLink>
            <StateLink
              to="home"
              params={{ noProviderRedirect: true }}
            >
              <Hoverable
                paddingX={24}
                paddingY={10}
              >
                <BodyText
                  appearance="small"
                  color={BrandColor.Metal}
                >
                  Directory
                </BodyText>
              </Hoverable>
            </StateLink>
            <StateLink
              to={PROFILE_EDIT_ROUTE}
              params={proParams}
            >
              <Hoverable
                paddingX={24}
                paddingY={10}
              >
                <BodyText
                  appearance="small"
                  color={BrandColor.Metal}
                >
                  Profile
                </BodyText>
              </Hoverable>
            </StateLink>
            <StateLink
              to="provider.settings_menu.account"
              params={proParams}
            >
              <Hoverable
                paddingX={24}
                paddingY={10}
              >
                <BodyText
                  appearance="small"
                  color={BrandColor.Metal}
                >
                  Account
                </BodyText>
              </Hoverable>
            </StateLink>
            <StateLink
              to="provider.settings_menu.more"
              params={proParams}
            >
              <Hoverable
                paddingX={24}
                paddingY={10}
              >
                <BodyText
                  appearance="small"
                  color={BrandColor.Metal}
                >
                  Settings
                </BodyText>
              </Hoverable>
            </StateLink>
            <StateLink
              href={addQueryStringParams('https://styleseat.freshdesk.com/support/home', utmParams)}
            >
              <Hoverable
                paddingX={24}
                paddingY={10}
              >
                <BodyText
                  appearance="small"
                  color={BrandColor.Metal}
                >
                  Help Center
                </BodyText>
              </Hoverable>
            </StateLink>
            <Touchable onClick={handleLogout}>
              <Hoverable
                paddingX={24}
                paddingY={10}
              >
                <BodyText
                  appearance="small"
                  color={BrandColor.Metal}
                >
                  Logout
                </BodyText>
              </Hoverable>
            </Touchable>
            {subProviders.length <= 1 ? null : (
              <Stack>
                <Box paddingX={24}>
                  <Box
                    paddingY={10}
                    borderTopWidth={1}
                    borderColor={BrandColor.Grey3}
                    borderStyle="solid"
                  >
                    <BodyText color={BrandColor.Metal} appearance="smaller" whiteSpace="nowrap">
                      SWITCH ACCOUNTS
                    </BodyText>
                  </Box>
                </Box>
                {subProviders.map(acct => (
                  <StateLink
                    key={acct.id}
                    to="."
                    params={{ providerId: acct.id }}
                  >
                    <Hoverable
                      paddingX={24}
                      paddingY={10}
                    >
                      <BodyText color={BrandColor.Metal} appearance="small" whiteSpace="nowrap">
                        {acct.name}
                      </BodyText>
                    </Hoverable>
                  </StateLink>
                ))}
              </Stack>
            )}
          </Stack>
        </Popover>
      ) : null}
    </Box>
  );
};

export default ProHeaderDropdown;
